import {
  Clock,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Vector3,
  Object3D,
  Mesh,
  WebGLRendererParameters,
  Material,
  Color,
  HemisphereLight,
  sRGBEncoding,
  PCFSoftShadowMap,
} from "three";
import { acceleratedRaycast } from "three-mesh-bvh";

//@ts-ignore
import Worker from "./workers/pose.worker";

import {
  Actor,
  ActorType,
  fetchActorResources,
  getPlayerType,
  PlayerType,
} from "./world/actor/actor";
import { PointSkeleton } from "../lib/misc/helpers";
import { JointMap, toEpoch, sleep } from "../lib/misc/util";
import { Context, PoserMode, PoserOptions } from "./context";
import { Gumbo, Player, SkeletalFrame } from "../lib/types/statsApi";
import { FrameManager, Frames } from "./util/frame";
import { CameraController, CameraMode } from "./camera/controller";
import { load } from "opentype.js";
import { makeGroundSpot } from "../lib/misc/position";

import StatsApi from "../api/StatsApi";
import {
  GameStateManager,
  timeInMsToGumboTimecode,
} from "./managers/GameStateManager";
import { DefensivePlayMode, PlayStateManager } from "./managers/playstate";
import { PoserClock } from "./util/clock";
import { TeamAndPlayerStateManager } from "./managers/TeamAndPlayerManager";
import {
  FieldVisionController,
  PublicCameraOption,
} from "./interface/FieldVisionController";
import { LoadingSpinner } from "./util/spinner";
import { EventList, TrackedEventsGenerator } from "./util/events";
import { MainChannel } from "./workers/channels/main";
import { RingBuffer } from "ring-buffer-ts";
import { GPULevel } from "./util/benchmark";
import { PoserMonitor } from "./util/monitor";
import { World } from "./world/world";
import { DEFAULT_ASSET_BASE } from "./constants";

// accelerate raycast
Mesh.prototype.raycast = acceleratedRaycast;

const identity = () => {};

const skeletalStructure = {
  Neck: ["Nose", "REye", "REar", "LEye", "LEar", "RShoulder", "LShoulder"],
  RShoulder: ["RHip", "RElbow"],
  RElbow: ["RWrist"],
  LShoulder: ["LHip", "LElbow"],
  LElbow: ["LWrist"],
  RHip: ["_waist", "RKnee"],
  LHip: ["LKnee"],
  RKnee: ["RAnkle"],
  LKnee: ["LAnkle"],
  RAnkle: ["RHeel"],
  LAnkle: ["LHeel"],
  RHeel: ["RBigToe", "RSmallToe"],
  LHeel: ["LBigToe", "LSmallToe"],
  RWrist: ["RThumb", "RPinky"],
  LWrist: ["LThumb", "LPinky"],
};

interface CameraChangeEvent {
  mode: PublicCameraOption;
  fromInteraction: boolean;
}

export interface PoserCallbacks {
  onGumboChange(gumbo: Gumbo): void;
  onCanPlay(): void;
  onCameraChange(e: CameraChangeEvent): void;
  onRenderLoopTick(): void;
}

export class PoserApp {
  private static canvas?: HTMLCanvasElement;
  private subscribers: Map<string, (context: Context) => void>;
  private pctLoadedSubscribers: Map<string, (pctLoaded: number) => void>;
  context: Context;

  private prevNowNum = -1;

  private actorsToAdd: { current: number; max: number };
  private actorRate: number;
  private maxFrameInterval: number;
  private stadiumLoadedCnt = { current: 0, max: 15 };
  private prevPlayerType?: PlayerType;

  private positions = {
    toChange: new Map<number, Player | undefined>(),
    toRemove: new Set<number>(),
  };

  private maxLiveDelta = 5; //s

  private windowDims: { width?: number; height?: number } = {};

  addSubscribers(name: string, cb: (context: Context) => void) {
    this.subscribers.set(name, cb);
  }

  addPctLoadedSubscribers(name: string, cb: (pctLoaded: number) => void) {
    this.pctLoadedSubscribers.set(name, cb);
  }

  constructor(options: PoserOptions) {
    const maxActorsToAdd = 1;
    const minFps = 10;

    const webGLRendererParameterOverrides =
      options.webGLRendererParameters || {};

    if (!PoserApp.canvas) {
      PoserApp.canvas = document?.createElement("canvas");
    }

    this.context = {
      scene: new Scene(),
      renderer: new WebGLRenderer(
        Object.assign(
          {
            antialias: true,
            powerPreference: "high-performance",
            canvas: PoserApp.canvas,
          },
          webGLRendererParameterOverrides
        )
      ),
      camera: new PerspectiveCamera(
        45,
        window.innerWidth / window.innerHeight,
        1,
        1500
      ),

      options: options,

      clock: new PoserClock(),
      workerChannel: new MainChannel(Worker()),
      // TODO: up this back to 1500 after debugging
      frames: new RingBuffer<SkeletalFrame>(1500),
      trackingFps: 30,

      debugClock: new Clock(),
      rafRequestId: -1,

      debugObjects: {},

      actors: new Map<number, Actor>(),
      stressDebugActors: [],

      modelType: ActorType.RBI,
      jointMap: new JointMap([
        (points) => {
          const lhip = points.get("LHip");
          const rhip = points.get("RHip");
          if (lhip && rhip) {
            points.set(
              "_waist",
              new Vector3().addVectors(lhip, rhip).divideScalar(2)
            );
          }
        },
      ]),

      gameState: new GameStateManager(),
      teamAndPlayerInfo: new TeamAndPlayerStateManager(),

      mainFrameManager: new FrameManager(),
      futureFrameManager: new FrameManager(Infinity),

      useStaticData: false,

      futureEventList: new EventList(),

      outfitTemplates: new Map(),
      materialsTemplates: new Map(),

      loadingSpinner: new LoadingSpinner(),

      callbacks: {
        onGumboChange: identity,
        onCanPlay: identity,
        onCameraChange: identity,
        onRenderLoopTick: identity,
      },

      state: {
        focus: {
          inFocus: true,
          wasPlaying: false,
        },

        playMode: DefensivePlayMode.BEGIN,

        model: true,
        skeleton: false,

        groundSpot: false,
        stadium: true,
        groundPlane: true,

        pointSkel: false,
        fabrikSkel: false,

        nameplatePositions: [1, 10],

        ballEnabled: true,
        padEnabled: true,
        trailEnabled: true,
        ballPadOpacity: 0.5,
        ballPadColor: 0xffffff,
        ballTrailColor: 0xbd9e4d,

        batEnabled: false,
        showBatPoints: false,

        positionId: 1,
        everyone: true,
        centralise: false,
        modelName: "generic/generic-lod.gltf",

        actorTransition: "none",
        actorSpotColor: 0x041e42,

        cameraMode: CameraMode.MAP,
        cameraAuxMode: "none",
        cameraFov: 45,

        printCam: false,
        boundariesVisible: false,
        stadiumCollision: true,

        playStateLead: 2,
        actionCamTransDuration: 2,

        now: 0,
        bufferStart: 0,
        bufferEnd: 0,

        debugSeeking: false,
        debugClock: false,

        playing: false,
        playbackSpeed: 1,

        stepSize: 0.5,
        stepping: false,
        stepTime: 0,

        playSelection: "",

        targetFps: 240,
        measureFps: false,

        recordingData: false,
        exportRecording: false,
        emulatePlayEvents: false,
        warnOnRemovePlayEvents: true,
        showBallOwner: false,

        resetBenchmark: false,

        stressMultiple: 1,
        lodLevel: 0,

        lightHelpersOn: false,

        lightTimeDebug: false,
        lightTimeOffset: 0,
        lightTimeUTC: "n/a",
        lightTimeLocal: "n/a",

        hemiIntensity: 0.5,
        hemiSkyColor: 0x2c56c0,
        hemiGroundColor: 0x253e25,

        sunX: 0,
        sunY: 0,
        sunZ: 0,
        sunIntensity: 4,
        bias: -0.0015,
        lightMargin: 500,
        lightNear: 0,
        lightFar: 2000,

        crowdEnergy: 0.5,
        crowdSpeed: 0.5,
      },
    };
    this.context.renderer.localClippingEnabled = true;

    this.context.callbacks = Object.assign(
      this.context.callbacks,
      options.callbacks
    );

    // TODO: let's increase rate when device != mobile
    this.actorsToAdd = { current: maxActorsToAdd, max: maxActorsToAdd };
    this.actorRate = 1 / 5;

    this.maxFrameInterval = 1 / minFps;

    this.subscribers = new Map();
    this.pctLoadedSubscribers = new Map();

    window.addEventListener("visibilitychange", this.visibilityCb);
    window.addEventListener("resize", this.windowResizeCb);
  }

  async init(container: HTMLElement): Promise<FieldVisionController> {
    // NB: below used to avoid GPU memory pipeline crash on iOS
    //     (https://discourse.threejs.org/t/textures-in-gltf-sometimes-display-black-but-only-on-ios/30520/26)
    // @ts-ignore
    window.createImageBitmap = undefined;

    const { camera, clock, debugClock, debugObjects, options, scene, state } =
      this.context;

    await this.configurePerformance(options.gpuLevel);

    const fieldVisionController = new FieldVisionController(this);

    debugClock.start();

    if (!options.assetBase) {
      options.assetBase = DEFAULT_ASSET_BASE;
    }

    if (options.loop != undefined) {
      clock.loop = options.loop;
    }

    if (options.batEnabled) {
      state.batEnabled = options.batEnabled;
    }

    if (options.playId) {
      if (options.playbackMode != undefined) {
        console.warn(
          `overriding poser mode specified in options (${options.playbackMode})`
        );
      }

      options.playbackMode = PoserMode.PLAY;
      options.playId = options.playId;
    } else if (options.seekTo) {
      if (options.playbackMode != undefined) {
        console.warn(
          `overriding poser mode specified in options (${options.playbackMode})`
        );
      }

      options.playbackMode = PoserMode.VOD;
    } else if (options.playbackMode == undefined) {
      // default to live
      options.playbackMode = PoserMode.LIVE;
    }

    this.context.container = container;

    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();

    this.windowDims.height = container.clientHeight;
    this.windowDims.width = container.clientWidth;

    this.context.cameraController = new CameraController(this.context);

    this.buildWorld();
    this.configureGraphics(container);

    const params = new URLSearchParams(window.location.search);
    const recording = !!params.get("recording");
    state.recordingData = recording;
    if (recording) {
      console.warn("recording frames - use only for debug");
    }

    // initialise maybes in context
    if (!debugObjects.pointSkel) {
      // initialise PointSkeleton
      const pointSkel = new PointSkeleton(skeletalStructure);
      scene.add(pointSkel);
      pointSkel.visible = false;
      debugObjects.pointSkel = pointSkel;
    }

    if (!debugObjects.groundSpot) {
      debugObjects.groundSpot = makeGroundSpot();
      scene.add(debugObjects.groundSpot);
      debugObjects.groundSpot.visible = state.groundSpot;
    }

    if (!this.context.playStateManager) {
      this.context.playStateManager = PlayStateManager.makeManager(
        this.context
      );
    }

    if (!this.context.eventGenerator) {
      this.context.eventGenerator = new TrackedEventsGenerator(this.context);
    }

    const fontFile = `https://www.mlbstatic.com/mlb.com/fonts/proxima-nova-semibold.woff`;

    load(fontFile, (err, font) => {
      if (err || !font) {
        console.error(`error loading ${fontFile}:`, err);
      } else {
        this.context.playerFont = font;
      }
    });

    const altFontFile = `https://www.mlbstatic.com/mlb.com/fonts/proxima-nova-bold.woff`;
    load(altFontFile, (err, font) => {
      if (err || !font) {
        console.error(`error loading ${fontFile}:`, err);
      } else {
        this.context.playerFontAlt = font;
      }
    });

    // fetch all resources and initialise things in order
    StatsApi.fetchBoxscore(options.gamePk)
      .then((boxscore) => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(15);
        }
        return this.context.teamAndPlayerInfo.setBoxscoreInformation(boxscore);
      })
      .then(() => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(30);
        }
        return this.context.gameState.update(this.context);
      })
      .then(() => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(45);
        }
        return fetchActorResources(this.context);
      })
      .then(() => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(60);
        }
        this.context.world = new World(this.context);
        return this.context.world.init();
      })
      .then(() => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(75);
        }
        return this.initWorker(options);
      })
      .then(() => {
        for (const cb of this.pctLoadedSubscribers.values()) {
          cb(85);
        }
        // start performance monitor
        this.context.monitor = new PoserMonitor(this.context);
      })
      .then(() => {
        [
          [1000, 90],
          [2000, 95],
          [3000, 100],
        ].forEach(([delay, pct]) => {
          setTimeout(() => {
            // report init ready to consumers
            if (pct === 100) {
              this.context.callbacks.onCanPlay();
            }
            for (const cb of this.pctLoadedSubscribers.values()) {
              cb(pct);
            }
          }, delay);
        });

        return this.renderLoop();
      })
      .catch((error) => console.error(error));

    return fieldVisionController;
  }

  teardown() {
    const {
      camera,
      cameraController,
      monitor,
      rafRequestId,
      renderer,
      scene,
      ui,
      workerChannel,
      world,
    } = this.context;

    cameraController?.teardown();

    world?.teardown();

    cancelAnimationFrame(rafRequestId);

    const disposeMats = (mat: Material) => {
      for (const key of Object.keys(mat)) {
        const value = mat[key];
        if (!value) continue;

        if (value.dispose && typeof value.dispose === "function") {
          value.dispose();
        }

        const mapTypes = [
          "map",
          "normalMap",
          "aoMap",
          "metalnessMap",
          "roughnessMap",
        ];
        for (const mapType of mapTypes) {
          if (
            value[mapType]?.dispose &&
            value[mapType].dispose === "function"
          ) {
            value[mapType].dispose();
          }
        }
      }

      mat.dispose();
    };
    const disposeObjects = (obj: any) => {
      if (obj.material) {
        if (obj.material instanceof Array) {
          for (const mat of obj.material) {
            disposeMats(mat);
          }
        } else {
          disposeMats(obj.material);
        }
      }
      if (obj.geometry) {
        obj.geometry?.dispose();
      }

      obj.removeFromParent();
    };

    const toCleanup: Object3D[] = [];

    scene.traverse((obj) => toCleanup.push(obj));
    camera.traverse((obj) => toCleanup.push(obj));

    while (toCleanup.length > 0) {
      disposeObjects(toCleanup.pop());
    }

    const gl = renderer.getContext();
    var numTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
    for (var unit = 0; unit < numTextureUnits; ++unit) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    renderer.dispose();
    renderer.renderLists.dispose();

    workerChannel.teardown();

    ui?.teardown();

    monitor?.teardown();

    for (const member in this.context) {
      this.context[member] = undefined;
    }

    window.removeEventListener("visibilitychange", this.visibilityCb);
    window.removeEventListener("resize", this.windowResizeCb);
  }

  async seekTo(realSeconds: number) {
    const { actors, clock, workerChannel } = this.context;
    console.log(`seeking to ${realSeconds}`);

    this.context.clock.real = realSeconds;

    // Clean up actors
    if (clock.atBufferLimit()) {
      for (const actor of actors.values()) {
        actor.teardown();
      }
      actors.clear();
    }

    // Refresh play state
    this.context.playStateManager = PlayStateManager.makeManager(this.context);

    workerChannel.update(clock);
    while (clock.atBufferLimit()) {
      await sleep(100);
    }
  }

  // callbacks
  private visibilityCb = () => {
    const { clock, options, state, workerChannel } = this.context;
    if (document.visibilityState === "hidden") {
      state.focus.inFocus = false;
      state.focus.wasPlaying = state.playing;

      state.playing = false;
    } else {
      state.focus.inFocus = true;

      if (options.playbackMode == PoserMode.LIVE) {
        const seekBackToLive = async () => {
          const limits = await workerChannel.getLimits();
          if (limits != undefined) {
            // compare live to current clock and see if we want to seek there
            const delta = limits.live - clock.real;
            console.log(
              `delta: ${delta}s / ${delta / 60}mins / ${delta / 60 / 60}hrs`
            );

            if (delta > this.maxLiveDelta) {
              this.seekTo(limits.live);
            }
          } else {
            console.warn("issue determining live time of game");
          }

          state.playing = state.focus.wasPlaying;
        };

        seekBackToLive();
      } else {
        state.playing = state.focus.wasPlaying;
      }
    }
  };

  private windowResizeCb = () => {
    if (!this.context.container) return;

    const width = this.context.container.clientWidth;
    const height = this.context.container.clientHeight;

    console.log(`resizing poser ${width} ${height}`);

    if (width == this.windowDims.width && height == this.windowDims.height) {
      return;
    }

    const { camera, renderer } = this.context;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    if (
      this.windowDims.width == undefined ||
      this.windowDims.height == undefined ||
      (this.windowDims.width <= width && this.windowDims.height <= height)
    ) {
      // on fullscreen transition, defer setSize call to fix ios crash
      setTimeout(() => {
        renderer.setSize(width, height);
      }, 100);
    } else {
      renderer.setSize(width, height);
    }

    this.windowDims.width = width;
    this.windowDims.height = height;
  };

  // main loop
  private renderLoop() {
    const { clock, gameState, monitor, state, workerChannel } = this.context;

    if (state.targetFps == 0) {
      console.error("invalid fps");
    }

    const fpsInterval = 1 / state.targetFps;

    monitor?.update(clock);

    clock.tick(state.playing || state.stepping);
    workerChannel.update(clock, monitor?.issues.workerSpeed);

    if (state.debugClock) {
      console.log(
        `clock relative: ${clock.relative} absolute: ${
          clock.real
        } utc: ${timeInMsToGumboTimecode(clock.realMs)}`
      );
    }

    gameState.update(this.context);

    clock.utcOffsetHr = gameState.getVenueUTCOffset();

    state.stepTime += clock.gameDelta;
    if (state.stepping && state.stepTime >= state.stepSize) {
      state.stepping = false;
      state.stepTime = 0;
    }

    // enforce target fps
    if (clock.elapsedTime > fpsInterval) {
      this.animateScene();

      // reset frame time monitor
      clock.resetReference(clock.elapsedTime % fpsInterval);
    }

    for (const cb of this.subscribers.values()) {
      cb(this.context);
    }

    this.context.callbacks.onRenderLoopTick();

    this.context.rafRequestId = requestAnimationFrame(() => this.renderLoop());
  }

  private animateScene() {
    const { actors, camera, cameraController, clock, renderer, scene } =
      this.context;

    const frameNum = this.updateTrackingDependencies();

    cameraController?.update(clock.delta, clock.gameDelta, frameNum);

    for (const actor of actors.values()) {
      actor.nameplate?.updateTransform(camera);
    }

    renderer.render(scene, camera);
  }

  private updateTrackingDependencies(): number | undefined {
    const {
      actors,
      clock,
      container,
      debugObjects,
      eventGenerator,
      frames,
      futureEventList,
      futureFrameManager,
      gameState,
      loadingSpinner,
      mainFrameManager,
      playStateManager,
      scene,
      state,
      ui,
      world,
    } = this.context;

    if (frames.getBufferLength() <= 0 || !world) {
      return undefined;
    }

    const lowFps = clock.elapsedTime > this.maxFrameInterval;

    let frameNum: number | undefined = undefined;

    loadingSpinner?.update(clock, container);

    state.now = clock.relative;
    state.bufferStart = Math.max(
      clock.bufferLimits[0] - clock.timelineLimits[0],
      0
    );
    state.bufferEnd = clock.bufferLimits[1] - clock.timelineLimits[0];

    // get and process frames in "future"
    const futureFrames = futureFrameManager.getFrames(frames);
    let nowFrames: Frames | undefined = undefined;

    // TODO: move futureFrameManager stuff to TrackingDataManager?
    // Emulate play events
    if (state.emulatePlayEvents) {
      eventGenerator?.writeEvents(futureFrames);
    }

    futureEventList.update(futureFrames);

    if (!clock.atBufferLimit()) {
      // get and process "now" frame
      nowFrames = mainFrameManager.getFrames(frames, clock.realMs);
      const currentFrame = nowFrames.current;

      if (currentFrame && nowFrames.next) {
        frameNum = currentFrame.num;

        if (frameNum != this.prevNowNum) {
          // TODO: move actor management into own class

          // add actors
          for (const position of currentFrame.positions) {
            const positionId = position.positionId;
            if (positionId == 0) {
              // position not gameplay related
              continue;
            }

            const player = gameState.getPlayerByPositionId(
              positionId,
              this.context
            );

            // see if we have an actor for this
            const actor = actors.get(positionId);

            const playerType = getPlayerType(positionId);
            // hide coaches and umpires for now
            if (
              playerType == PlayerType.COACH ||
              playerType == PlayerType.UMPIRE ||
              playerType == PlayerType.PLATE_UMPIRE
            ) {
              continue;
            }

            if (
              !actor ||
              (player && !actor.isOrWillBePlayer(player)) ||
              !actor.isPartOfScene
            ) {
              this.positions.toChange.set(positionId, player);
            }
          }
        }

        // add / change actors - rate limited and only after stadium has loaded
        // TODO: move actor management into own class
        const stadiumLoaded =
          world?.stadium?.loaded &&
          this.stadiumLoadedCnt.current++ >= this.stadiumLoadedCnt.max;
        if (this.actorsToAdd.current >= 1 && !lowFps && stadiumLoaded) {
          const ids = [...this.positions.toChange.keys()].sort((a, b) => b - a);
          for (const id of ids) {
            const player = this.positions.toChange.get(id);
            const playerType = getPlayerType(id);

            if (
              playerType != this.prevPlayerType &&
              this.actorsToAdd.current-- < 1
            ) {
              break;
            }

            let actor = actors.get(id);
            if (actor) {
              if (player) {
                actor.setPlayer(player);
              }
            } else {
              actor = new Actor(this.context, id, player);
              actors.set(id, actor);
            }

            if (!actor.isPartOfScene) {
              actor.addToScene(scene);
            }

            this.positions.toChange.delete(id);

            this.prevPlayerType = playerType;
          }
        }

        // remove actors - assuming no rate limiting required
        for (const positionId of this.positions.toRemove) {
          const actor = actors.get(positionId);
          actor?.removeFromScene(scene);

          this.positions.toRemove.delete(positionId);
        }

        // ui positions list
        const positions = new Set<number>();
        for (const actor of actors.values()) {
          if (actor.isPartOfScene) {
            positions.add(actor.id);
          }
        }
        ui?.setPositions([...positions]);

        // Actor
        for (const actor of actors.values()) {
          const current = actor.id == state.positionId;
          if (state.everyone || current) {
            actor.update(nowFrames, clock, current ? debugObjects : undefined);
          }
        }

        if (frameNum != this.prevNowNum) {
          // retire actors
          for (const actor of actors.values()) {
            if (
              actor.isPartOfScene &&
              actor.tooOld() &&
              !this.positions.toChange.has(actor.id)
            ) {
              this.positions.toRemove.add(actor.id);
            }
          }
        }

        this.actorsToAdd.current = Math.min(
          this.actorsToAdd.current + this.actorRate,
          this.actorsToAdd.max
        );

        this.prevNowNum = currentFrame.num;
      }

      // Play state machine
      const inInning = gameState.inningOngoing();
      playStateManager?.update(nowFrames, futureEventList, inInning);
    }

    world.update(clock, gameState, nowFrames);

    return frameNum;
  }

  private async configurePerformance(gpuTierOverride?: string) {
    // TEMP: turning down GPU level for everyone for now
    this.context.options.gpuLevel = GPULevel.LOW;
    this.context.state.lodLevel = 3;

    // this.context.options.gpuLevel = await GPULevelUtil.get(gpuTierOverride);
    // if (this.context.options.gpuLevel != GPULevel.HIGH) {
    //   this.context.state.lodLevel = 3;
    // }
  }

  private async initWorker(options: PoserOptions) {
    const limits = await this.context.workerChannel.init(this.context);
    if (limits == undefined) {
      console.error("no live value returned from data manager");
    } else {
      if (options.playbackMode == PoserMode.LIVE) {
        console.log("seeking to live");
        return this.seekTo(limits.live);
      } else if (
        options.playbackMode == PoserMode.PLAY &&
        this.context.clock.playLimits
      ) {
        console.log(
          "seeking to start of requested playId: ",
          options.playId,
          this.context.clock.playLimits[0]
        );
        return this.seekTo(this.context.clock.playLimits[0]);
      } else if (options.playbackMode == PoserMode.VOD) {
        if (options.seekTo) {
          return this.seekTo(toEpoch(options.seekTo) / 1000);
        } else {
          console.warn("no seekTo specified, seeking to start");
          return this.seekTo(limits.start);
        }
      }
    }
  }

  private buildWorld() {
    const { scene, state } = this.context;

    scene.background = new Color(0xa0a0a0);

    const hemiLight = new HemisphereLight(
      state.hemiSkyColor,
      state.hemiGroundColor,
      state.hemiIntensity
    );
    hemiLight.position.set(0, 120, 0);
    scene.add(hemiLight);
    this.context.hemiLight = hemiLight;
  }

  private configureGraphics(container: HTMLElement) {
    const { camera, renderer } = this.context;
    const width = container.clientWidth;
    const height = container.clientHeight;

    renderer.physicallyCorrectLights = true;
    renderer.setPixelRatio(window.devicePixelRatio);
    camera.aspect = width / height;
    renderer.setSize(width, height);
    renderer.outputEncoding = sRGBEncoding;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    const betaContainer = document.createElement("div");
    betaContainer.setAttribute(
      "style",
      "position: absolute; font-size: 14px; top: 5px; width: 100%; text-align: center; display: block; user-select: none; pointer-events: none;"
    );
    const beta = document.createElement("div");
    beta.innerHTML = "BETA";
    beta.setAttribute(
      "style",
      "padding: 4px 8px; background-color: black; color: white; display: inline-block; opacity: 0.3; border-radius: 5px;"
    );
    betaContainer.appendChild(beta);
    container.appendChild(betaContainer);
  }
}
